import styled from '@emotion/styled';
import { Constants, Theme } from '@seedlang/constants';
import { AppUI } from '@seedlang/state';
import { LanguageStore, LevelStore, WordTypeStore, WorksheetWordStore } from '@seedlang/stores';
import { isPresent } from '@seedlang/utils';
import autobind from 'autobind-decorator';
import SortableColumns from 'components/hoc/sortable_columns';
import InfoTooltip from 'components/info_tooltip';
import Modal from 'components/modal';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import WorksheetWordEdit from 'pages/creator/worksheets/worksheet_word_edit';
import WorksheetWordIndexRow from 'pages/creator/worksheets/worksheet_word_index_row';
import React from 'react';

const Wrapper = styled.div`
  .word-index {
    .prefix-wrapper {
      display: inline-block;
      font-size: 12px;
      cursor: pointer;
      width: 60px;
      .text {
        display: block;
      }
      .remove {
        display: none;
      }
      .fa-download {
        color: ${Theme.red};
        font-size: 12px;
      }
    }
    .prefix-wrapper:hover {
      .text {
        display: none;
      }
      .remove {
        text-decoration: underline;
        display: block;
      }
    }
  }
`;

@observer
class WorksheetWordIndex extends React.Component {

  @observable editWorksheetWordId;

  componentDidMount() {
    this.editWorksheetWordId = null;
    if (!LevelStore.hasIndexData) {
      LevelStore.getIndex();
    }
    if (!WordTypeStore.hasIndexData) {
      WordTypeStore.getIndex();
    }
    if (AppUI.site.languageId) {
      LanguageStore.getShow({ids: {languageId: AppUI.site.languageId}});
    }
  }

  componentWillUnmount() {
    this.editWorksheetWordId = null;
  }

  @autobind onDelete(id) {
    WorksheetWordStore.destroy({ids: {worksheetWordId: id}}, this.props.afterChange);
  }

  @autobind toggleWordEditModal(worksheetWord) {
    if (isPresent(this.editWorksheetWord)) {
      this.editWorksheetWordId = null;
    } else {
      this.editWorksheetWordId = worksheetWord.id;
    }
  }

  @computed get editWorksheetWord() {
    return WorksheetWordStore.hasIndexData && WorksheetWordStore.indexData.find(item => item.id === this.editWorksheetWordId);
  }

  @computed get registerOptions() {
    const registerOptions = Constants.REGISTER_OPTIONS;
    if (AppUI.site.languageId === 'DE' || AppUI.siteIsDefault) {
        registerOptions.push(['austrian', 'Austrian']);
        registerOptions.push(['swiss', 'Swiss']);
    } return registerOptions;
  }

  render() {
    return (
      <Wrapper>
        {
          isPresent(this.editWorksheetWordId) && isPresent(this.editWorksheetWord) &&
          <Modal
            onCloseModal={this.toggleWordEditModal}
            iconFill='#000'
            top='300px'
            width='800px'
          >
            <WorksheetWordEdit
              worksheetWord={this.editWorksheetWord}
              afterChange={this.props.afterChange}
            />
          </Modal>
        }
        <div className='word-index'>
          <table className='table-wrapper admin-table'>
            <thead>
            <tr>
              {
                !this.props.hidePositions &&
                  <th width='15'># <InfoTooltip>Chronological Positions</InfoTooltip></th>
              }
              <th width='15' />{/* External Info */}
              <th width='50'>Type</th>
              <th width='100'>Target</th>
              {
                AppUI.site.showAlternateTargetText &&
                <th width='100'>Romanized</th>
              }
              {
                !AppUI.site.languageId?.startsWith('EN') &&
                  <th width='120'>Source</th>
              }
              {
                AppUI.site.languageId?.startsWith('EN') &&
                  <th width='350'>Definition</th>
              }
              <th width='60'>Plural</th>
              {/*<th*/}
              {/*  width="100"*/}
              {/*  data-tip*/}
              {/*  data-for="gender-var-heading"*/}
              {/*>*/}
              {/*  <span>Gender var.</span>*/}
              {/*  <InfoTooltip*/}
              {/*    margin="1px 0 0px 5px"*/}
              {/*  >*/}
              {/*    For example in German, <i>Schülerin</i> is the gender variant of <i>Schüler</i>.*/}
              {/*  </InfoTooltip>*/}
              {/*</th>*/}
              {
                isPresent(LanguageStore.showData.genderOptions) &&
                  <th width='50'>Gender</th>
              }
              <th width='60'>Register</th>
              {
                !AppUI.siteIsDefault && !AppUI.site.language?.hasLessons &&
                  <th width='100'>Tags</th>
              }
              <th width={AppUI.groupId === Constants.EASY_GERMAN_GROUP_ID ? '20' : '100'}>Level</th>
              {
                (AppUI.siteIsDefault || LanguageStore.showData.hasLessons) &&
                  <th width='80'>Has Video</th>
              }
              <th width='16' />
            </tr>
            </thead>
            <tbody>
            {
              WorksheetWordStore.indexData.map((item, index) => {
                return (
                  <WorksheetWordIndexRow
                    key={`${item.id}-${index}`}
                    {...this.props}
                    highlighted={isPresent(this.props.highlightedWordIds) ? this.props.highlightedWordIds.includes(item.word.id) : false}
                    worksheetWord={item}
                    afterChange={this.props.afterChange}
                    onDelete={this.onDelete}
                    genderOptions={LanguageStore.showData.genderOptions}
                    showAlternateTargetText={AppUI.site.showAlternateTargetText}
                    toggleWordEditModal={this.toggleWordEditModal}
                    showHasVideo={AppUI.siteIsDefault || LanguageStore.showData.hasLessons}
                    registerOptions={this.registerOptions}
                  />
                );
              })
            }
            </tbody>
          </table>
        </div>
      </Wrapper>
    );
  }
}

export default SortableColumns(WorksheetWordIndex, WorksheetWordStore, { sortColumn: 'position', sortIsAscending: true });
